import React from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { RESPONSIVE, useResponsive } from '@services/responsiveProvider';
import getRoute, { QUERY_ENUM, routeRegex, useTestRoute } from '@services/routeList';
import MobileTabs from '@features/ui/components/Tabs/Mobile';
import DesktopTabs from '@features/ui/components/Tabs/Desktop';
import Card from '@features/ui/components/Card';
import { selectSerieEvents, selectSeriePacks, selectSerieRegistrationsOpen } from '@features/serieDetail/slices/serieDetailServerSlice';

const SerieTabs = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const serieSlug = router.query[QUERY_ENUM.serieSlug] as string;
  const routeTester = useTestRoute();
  const responsive = useResponsive();

  const registrationsOpen = useSelector(selectSerieRegistrationsOpen);
  const packs = useSelector(selectSeriePacks);
  const events = useSelector(selectSerieEvents);

  const serieEventsWithResults = events.filter(event => event.hasSerieResults);

  const subscriptionSubTitle = t(`serieTabs_tabSubTitleSubscriptions_${registrationsOpen ? 'OPENED' : 'CLOSED'}.message`);

  const tabs = [
    {
      title: t('serieTabs_tabTitleDescription.message'),
      href: getRoute.series(serieSlug).description(),
      routeTest: routeRegex.serieDescription,
      qaId: 'description',
    },
    {
      title: t('serieTabs_tabTitleEvents.message'),
      href: getRoute.series(serieSlug).events(),
      routeTest: routeRegex.serieEvents,
      qaId: 'events',
    },
    {
      title: t('serieTabs_tabTitleSubscriptions.message'),
      href: getRoute.series(serieSlug).subscriptions(),
      routeTest: routeRegex.serieSubscriptions,
      subTitle: subscriptionSubTitle,
      disabled: packs.length === 0,
      qaId: 'subscriptions',
    },
    {
      title: t('eventTabs_tabTitleRanking.message'),
      href: getRoute.series(serieSlug).results(),
      disabled: serieEventsWithResults.length === 0,
      subTitle: serieEventsWithResults.length > 0
        ? t('eventTabs_tabTitleRankingOpen.message')
        : '',
      routeTest: routeRegex.serieResults,
      qaId: 'ranking',
    },
  ].filter(Boolean);

  const currentIndex = tabs.findIndex(tab => tab.routeTest && routeTester(tab.routeTest));

  if (responsive === RESPONSIVE.DESKTOP) {
    return (
      <DesktopTabsWrapper>
        <DesktopBar />
        <DesktopTabs tabs={tabs} currentIndex={currentIndex >= 0 ? currentIndex : 0} />
      </DesktopTabsWrapper>
    );
  }

  return <StyledMobileTabs tabs={tabs} currentIndex={currentIndex >= 0 ? currentIndex : 0} />;
};

const StyledMobileTabs = styled(MobileTabs)`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const DesktopTabsWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const DesktopBar = styled(Card)`
  position: absolute;
  height: 100%;
  top: 0;
  /* magic trick */
  width: 200vw;
  left: -100vw;
`;

export default SerieTabs;
